import { exponeaCapture } from '@/components/hooks/exponeaCapture';

const BOOKING_TRACKING_SESSION_KEY = 'affideaBookingTracking';
const BOOKING_TRACKING_SENT_PREFIX = 'affideaBookingTrackingSent:';
const EXPONEA_TRACKING_SESSION_KEY = 'exponeaTrack';
const EXPONEA_TRACKING_SENT_PREFIX = 'affideaExponeaBookingSent:';

const isBrowser = () => typeof window !== 'undefined';

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
};

const toPositiveNumber = (value, fallback = 1) => {
  const number = toNumber(value);
  return number !== undefined && number > 0 ? number : fallback;
};

const compact = (value) =>
  Object.entries(value).reduce((acc, [key, val]) => {
    if (val !== undefined && val !== null && val !== '') {
      acc[key] = val;
    }
    return acc;
  }, {});

const padTwo = (value) => String(value).padStart(2, '0');

const formatSlotDateTime = (slot) => {
  if (!slot?.startDate) {
    return {};
  }

  const date = new Date(slot.startDate);

  if (Number.isNaN(date.getTime())) {
    return {};
  }

  return {
    appointment_date: [
      date.getFullYear(),
      padTwo(date.getMonth() + 1),
      padTwo(date.getDate()),
    ].join('-'),
    appointment_time: `${padTwo(date.getHours())}:${padTwo(
      date.getMinutes(),
    )}`,
  };
};

const getServiceName = (service) =>
  service?.serviceTitle ||
  service?.serviceTypeTitle ||
  service?.service?.title ||
  service?.title;

const getSpecialty = (service) =>
  service?.specialty || service?.cardTitle || service?.serviceTypeTitle;

const getPrice = (service, selectedTimeSlot) => {
  if (selectedTimeSlot?.isExtra) {
    return toPositiveNumber(service?.extraPrice ?? service?.priceWithDiscount);
  }

  return toPositiveNumber(service?.priceWithDiscount ?? service?.price);
};

const toExponeaVariables = (payload) =>
  Object.entries(payload).map(([name, value]) => ({ name, value }));

export const pushDataLayerEvent = (payload, { clearEcommerce = false } = {}) => {
  if (!isBrowser() || !payload?.event) {
    return;
  }

  window.dataLayer = window.dataLayer || [];

  if (clearEcommerce) {
    window.dataLayer.push({ ecommerce: null });
  }

  window.dataLayer.push(compact(payload));
};

export const buildBookingItem = (service, selectedTimeSlot) => {
  const serviceName = getServiceName(service);
  const price = getPrice(service, selectedTimeSlot);

  return compact({
    item_id:
      service?.serviceExternalId ||
      service?.externalId ||
      service?.serviceId ||
      service?.id,
    item_name: serviceName,
    item_category: getSpecialty(service),
    price,
    quantity: 1,
    center_city: service?.cityTitle,
    center_name: service?.departmentTitle || service?.workplaceTitle,
    specialty: getSpecialty(service),
    service_name: serviceName,
    doctor_name: service?.specialistName,
  });
};

export const buildBookingTrackingData = ({
  services,
  selectedTimeSlot,
  registrationId,
  orderId,
  paymentInfo,
} = {}) => {
  const items = (Array.isArray(services) ? services : [services])
    .filter(Boolean)
    .map((service) => buildBookingItem(service, selectedTimeSlot));

  const value =
    toNumber(paymentInfo?.computedTotal) ??
    items.reduce((sum, item) => sum + toPositiveNumber(item.price), 0);

  return compact({
    registrationId,
    orderId,
    bookingId: orderId || registrationId,
    value,
    currency: 'EUR',
    items,
    service_name: items[0]?.service_name,
    ...formatSlotDateTime(selectedTimeSlot),
  });
};

export const buildCouponTrackingData = ({
  coupon,
  orderId,
  quantity = 1,
  value,
} = {}) => {
  const count = toPositiveNumber(quantity);
  const price = toPositiveNumber(coupon?.price);
  const total = toNumber(value) ?? price * count;
  const item = compact({
    item_id: coupon?.couponId || coupon?.id,
    item_name: coupon?.title,
    item_category: 'Dovanų kuponai',
    price,
    quantity: count,
    service_name: coupon?.title,
  });

  return compact({
    registrationId: orderId,
    orderId,
    bookingId: orderId,
    value: total,
    currency: 'EUR',
    items: [item],
    service_name: coupon?.title,
  });
};

export const buildComplexTrackingData = ({
  complex,
  complexPrice,
  orderId,
} = {}) => {
  const price = toPositiveNumber(complexPrice?.price ?? complexPrice?.amount);
  const item = compact({
    item_id: complex?.service?.serviceId || complex?.serviceId || complex?.id,
    item_name: complex?.service?.title || complex?.title,
    item_category: 'Paslaugų paketai',
    price,
    quantity: 1,
    center_name: complexPrice?.departmentTitle || complexPrice?.departmentName,
    service_name: complex?.service?.title || complex?.title,
  });

  return compact({
    registrationId: orderId,
    orderId,
    bookingId: orderId,
    value: price,
    currency: 'EUR',
    items: [item],
    service_name: item.service_name,
  });
};

export const persistBookingTrackingData = (data) => {
  if (!isBrowser() || !data?.bookingId) {
    return;
  }

  sessionStorage.setItem(BOOKING_TRACKING_SESSION_KEY, JSON.stringify(data));
};

export const readBookingTrackingData = () => {
  if (!isBrowser()) {
    return null;
  }

  try {
    const raw = sessionStorage.getItem(BOOKING_TRACKING_SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const clearBookingTrackingData = () => {
  if (isBrowser()) {
    sessionStorage.removeItem(BOOKING_TRACKING_SESSION_KEY);
  }
};

export const pushViewServiceEvents = ({ serviceTypeId, serviceTitle }) => {
  if (!serviceTitle) {
    return;
  }

  pushDataLayerEvent({
    event: 'view_service',
    service_name: serviceTitle,
    specialty: serviceTitle,
  });

  pushDataLayerEvent(
    {
      event: 'view_item',
      ecommerce: {
        currency: 'EUR',
        value: 1,
        items: [
          compact({
            item_id: serviceTypeId,
            item_name: serviceTitle,
            item_category: serviceTitle,
            price: 1,
            quantity: 1,
          }),
        ],
      },
    },
    { clearEcommerce: true },
  );
};

export const pushExponeaBookingStartEvent = ({ service, selectedTimeSlot }) => {
  exponeaCapture(
    toExponeaVariables({
      booking_appointment_date: formatSlotDateTime(selectedTimeSlot)
        .appointment_date,
      booking_appointment_time: formatSlotDateTime(selectedTimeSlot)
        .appointment_time,
      product_id: service?.serviceId,
      category_id: service?.categoryId,
      booking_stage: 'Booking process started',
    }),
    'booking_start',
  );
};

export const pushServiceSelectionEvents = ({ service, selectedTimeSlot }) => {
  const item = buildBookingItem(service, selectedTimeSlot);
  const slotData = formatSlotDateTime(selectedTimeSlot);

  pushDataLayerEvent({
    event: 'select_datetime',
    center_city: item.center_city,
    center_name: item.center_name,
    specialty: item.specialty,
    service_name: item.service_name,
    doctor_name: item.doctor_name,
    ...slotData,
  });

  pushDataLayerEvent({
    event: 'start_booking',
    center_city: item.center_city,
    center_name: item.center_name,
    specialty: item.specialty,
    service_name: item.service_name,
    doctor_name: item.doctor_name,
  });

  pushDataLayerEvent(
    {
      event: 'add_to_cart',
      ecommerce: {
        currency: 'EUR',
        value: item.price,
        items: [item],
      },
    },
    { clearEcommerce: true },
  );
};

export const pushBeginCheckoutEvent = ({ services, selectedTimeSlot }) => {
  const items = (Array.isArray(services) ? services : [services])
    .filter(Boolean)
    .map((service) => buildBookingItem(service, selectedTimeSlot));

  if (!items.length) {
    return;
  }

  pushDataLayerEvent(
    {
      event: 'begin_checkout',
      ecommerce: {
        currency: 'EUR',
        value: items.reduce((sum, item) => sum + toPositiveNumber(item.price), 0),
        items,
      },
    },
    { clearEcommerce: true },
  );
};

export const pushBeginCheckoutTrackingData = (trackingData) => {
  if (!trackingData?.items?.length) {
    return;
  }

  pushDataLayerEvent(
    {
      event: 'begin_checkout',
      ecommerce: {
        currency: trackingData.currency || 'EUR',
        value: trackingData.value,
        items: trackingData.items,
      },
    },
    { clearEcommerce: true },
  );
};

export const buildExponeaBookingPayload = ({
  services,
  selectedTimeSlot,
  registrationInput,
  registrationId,
  orderId,
  paymentInfo,
} = {}) => {
  const service = Array.isArray(services) ? services[0] : services;
  const isService = !service?.specialistId;
  const category2 = isService
    ? service?.serviceTitle
    : `${service?.specialistName || ''} - ${service?.serviceTitle || ''}`.trim();
  const price =
    toNumber(paymentInfo?.computedTotal) ?? getPrice(service, selectedTimeSlot);
  const bookingId = orderId || registrationId;

  return toExponeaVariables(
    compact({
      category_1: service?.serviceTypeTitle,
      category_2: category2,
      price,
      'registration type': registrationInput?.PaymentType,
      firstName: registrationInput?.PatientName,
      lastName: registrationInput?.PatientSurname,
      email: registrationInput?.PatientEmail,
      product_id: service?.serviceId,
      category_id: service?.categoryId,
      mobile_phone: registrationInput?.PatientPhoneNumber,
      childName: registrationInput?.ChildIdNumber
        ? registrationInput?.ChildName
        : undefined,
      childSurname: registrationInput?.ChildIdNumber
        ? registrationInput?.ChildSurname
        : undefined,
      booking_appointment_id: selectedTimeSlot?.timeSlotId,
      booking_booking_id: bookingId,
      booking_payment_gateway_id: bookingId,
      purchase_total_cost: price,
    }),
  );
};

export const buildCouponExponeaPayload = ({
  coupon,
  formData,
  quantity = 1,
  orderId,
} = {}) => {
  const count = toPositiveNumber(quantity);
  const total = toPositiveNumber(coupon?.price) * count;

  return toExponeaVariables(
    compact({
      category_1: 'Dovanų kuponai',
      category_2: coupon?.title,
      Firstname: formData?.patientFirstName || formData?.payerFirstName,
      Lastname: formData?.patientLastName || formData?.payerLastName,
      email: formData?.payerEmail,
      Count: count,
      Price: total,
      Payment: 'Paysera',
      product_id: coupon?.couponId,
      category_id: 3,
      mobile_phone: formData?.payerPhone,
      booking_booking_id: orderId,
      booking_payment_gateway_id: orderId,
      purchase_total_cost: total,
    }),
  );
};

export const buildComplexExponeaPayload = ({
  complex,
  complexPrice,
  formData,
  orderId,
} = {}) => {
  const price = toPositiveNumber(complexPrice?.price);

  return toExponeaVariables(
    compact({
      category_1: 'Paslaugų paketai',
      category_2: complex?.service?.title,
      Firstname: formData?.payerFirstName,
      Lastname: formData?.payerLastName,
      email: formData?.payerEmail,
      Count: 1,
      Price: price,
      Payment: 'Paysera',
      product_id: complex?.service?.serviceId,
      category_id: 3,
      mobile_phone: formData?.payerPhone,
      booking_booking_id: orderId,
      booking_payment_gateway_id: orderId,
      purchase_total_cost: price,
    }),
  );
};

export const persistExponeaBookingPayload = (payload) => {
  if (isBrowser() && Array.isArray(payload) && payload.length > 0) {
    sessionStorage.setItem(EXPONEA_TRACKING_SESSION_KEY, JSON.stringify(payload));
  }
};

export const readExponeaBookingPayload = () => {
  if (!isBrowser()) {
    return [];
  }

  try {
    return JSON.parse(
      sessionStorage.getItem(EXPONEA_TRACKING_SESSION_KEY) || '[]',
    );
  } catch {
    return [];
  }
};

export const clearExponeaBookingPayload = () => {
  if (isBrowser()) {
    sessionStorage.removeItem(EXPONEA_TRACKING_SESSION_KEY);
  }
};

export const pushExponeaBookingItemEvent = (payload) => {
  if (Array.isArray(payload) && payload.length > 0) {
    exponeaCapture(payload, 'booking_item');
  }
};

export const pushExponeaBookingResultEvent = (payload, status) => {
  if (!Array.isArray(payload) || payload.length === 0) {
    return;
  }

  const bookingId = payload.find((item) => item.name === 'booking_booking_id')
    ?.value;
  const sentKey = `${EXPONEA_TRACKING_SENT_PREFIX}${bookingId || status}`;

  if (isBrowser() && sessionStorage.getItem(sentKey) === 'true') {
    return;
  }

  exponeaCapture(
    [
      ...payload,
      { name: 'Registration status', value: status },
      {
        name: 'booking_stage',
        value: status === 'Success' ? 'Booked & Paid' : 'Cancelled',
      },
    ],
    'booking',
  );

  if (isBrowser()) {
    sessionStorage.setItem(sentKey, 'true');
  }
};

export const pushAddUserDataEvent = ({ services, selectedTimeSlot }) => {
  const service = Array.isArray(services) ? services[0] : services;
  const item = buildBookingItem(service, selectedTimeSlot);

  pushDataLayerEvent({
    event: 'add_user_data',
    form_status: 'initialised',
    doctor_name: item.doctor_name,
    ...formatSlotDateTime(selectedTimeSlot),
  });
};

export const pushBookingConfirmedEvents = (trackingData) => {
  if (!trackingData?.bookingId) {
    return;
  }

  const sentKey = `${BOOKING_TRACKING_SENT_PREFIX}${trackingData.bookingId}`;

  if (isBrowser() && sessionStorage.getItem(sentKey) === 'true') {
    return;
  }

  pushDataLayerEvent(
    {
      event: 'purchase',
      ecommerce: {
        transaction_id: trackingData.bookingId,
        value: trackingData.value,
        currency: trackingData.currency || 'EUR',
        items: trackingData.items || [],
      },
    },
    { clearEcommerce: true },
  );

  pushDataLayerEvent({
    event: 'generate_lead',
    form_name: 'booking_checkout',
    lead_id: trackingData.bookingId,
  });

  pushDataLayerEvent({
    event: 'booking_confirmed',
    booking_id: trackingData.bookingId,
    value: trackingData.value,
    currency: trackingData.currency || 'EUR',
    service_name: trackingData.service_name,
  });

  if (isBrowser()) {
    sessionStorage.setItem(sentKey, 'true');
  }
};
